import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Check, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { listMessages, deleteMessage, markMessageRead } from "@/lib/api";

export default function AdminMessages() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try { setItems(await listMessages()); } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); }, []);

    const onDelete = async (id) => {
        if (!confirm("Delete this message?")) return;
        try { await deleteMessage(id); toast.success("Deleted"); refresh(); } catch { toast.error("Failed"); }
    };

    const onRead = async (id) => {
        try { await markMessageRead(id); refresh(); } catch { toast.error("Failed"); }
    };

    return (
        <div className="space-y-6">
            <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Inbox</p>
                <h1 className="mt-1 font-display text-3xl sm:text-4xl">Messages</h1>
            </div>
            {loading ? <p className="text-paper/70">Loading…</p> : (
                <div className="space-y-3">
                    {items.length === 0 && (
                        <Card className="p-8 text-center rounded-2xl bg-white/[0.03] border-white/10 text-paper/70">No messages yet.</Card>
                    )}
                    {items.map((m) => (
                        <Card key={m.id} className={`p-5 rounded-2xl border-white/10 ${m.read ? "bg-white/[0.03]" : "bg-white/[0.06]"}`} data-testid="admin-message-item">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-display text-lg">{m.subject || "(no subject)"}</p>
                                        {!m.read && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sea-glass))]" />}
                                    </div>
                                    <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{m.name} · <a className="link-underline" href={`mailto:${m.email}`}>{m.email}</a> · {new Date(m.created_at).toLocaleString()}</p>
                                    <p className="mt-3 text-sm text-paper/80 whitespace-pre-wrap">{m.message}</p>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    <Button size="sm" variant="ghost" className="text-paper/80 hover:bg-white/[0.06]" asChild>
                                        <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "")}`}><Mail className="h-4 w-4" /></a>
                                    </Button>
                                    {!m.read && <Button size="sm" variant="ghost" className="text-paper/80 hover:bg-white/[0.06]" onClick={() => onRead(m.id)}><Check className="h-4 w-4" /></Button>}
                                    <Button size="sm" variant="ghost" className="text-paper/80 hover:text-red-300" onClick={() => onDelete(m.id)}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
